import { create } from "zustand";
import {
  fetchTaxOfficeData,
  submitTaxOfficeData,
} from "@pages/Onboarding/api/taxOfficeApi";
import type { TaxOfficeData } from "@pages/Onboarding/api/taxOfficeApi";
import { refreshIntercomUserContext } from "@/utils/intercom";

interface TaxOfficeState {
  data: TaxOfficeData | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchData: () => Promise<void>;
  updateField: <K extends keyof TaxOfficeData>(
    field: K,
    value: TaxOfficeData[K]
  ) => Promise<void>;
  updateFields: (fields: Partial<TaxOfficeData>) => Promise<void>;
  setData: (data: TaxOfficeData) => void;
  reset: () => void;
}

const initialState = {
  data: null,
  isLoading: false,
  error: null,
};

/**
 * Zustand store for managing Tax Office data.
 * Provides methods to fetch, update, and persist tax office information
 * including PTIN, EFIN, EIN, firm details, and software type.
 */
export const useTaxOfficeStore = create<TaxOfficeState>()((set, get) => ({
  ...initialState,

  /**
   * Fetches tax office data from the backend API.
   * Sets loading state while fetching and updates the store on success.
   */
  fetchData: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await fetchTaxOfficeData();
      set({ data, isLoading: false });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch tax office data";
      set({ error: errorMessage, isLoading: false });
      console.error("Failed to fetch tax office data:", error);
    }
  },

  /**
   * Updates a single field in the tax office data and persists to backend.
   * Performs optimistic update - immediately updates local state,
   * then syncs with backend. Reverts on failure.
   *
   * @param field - The field to update
   * @param value - The new value for the field
   */
  updateField: async <K extends keyof TaxOfficeData>(
    field: K,
    value: TaxOfficeData[K]
  ) => {
    const currentData = get().data;
    const newData = { ...currentData, [field]: value } as TaxOfficeData;

    // Optimistic update
    set({ data: newData, error: null });

    try {
      await submitTaxOfficeData({ [field]: value });

      // Refresh Intercom if tax software or intake system changed
      if (field === "softwareType" || field === "intakeSoftware") {
        refreshIntercomUserContext("settings").catch((error) => {
          console.error("[TaxOfficeStore] Failed to refresh Intercom:", error);
          // Don't throw - this is a background operation
        });
      }
    } catch (error) {
      // Revert on failure
      set({ data: currentData });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update tax office data";
      set({ error: errorMessage });
      console.error(`Failed to update ${String(field)}:`, error);
      throw error;
    }
  },

  /**
   * Updates multiple fields in the tax office data and persists to backend.
   * Performs optimistic update - immediately updates local state,
   * then syncs with backend. Reverts on failure.
   *
   * @param fields - Object containing fields to update
   */
  updateFields: async (fields: Partial<TaxOfficeData>) => {
    const currentData = get().data;
    const newData = { ...currentData, ...fields } as TaxOfficeData;

    // Optimistic update
    set({ data: newData, error: null });

    try {
      await submitTaxOfficeData(fields);

      // Refresh Intercom if tax software or intake system changed
      if ("softwareType" in fields || "intakeSoftware" in fields) {
        refreshIntercomUserContext("settings").catch((error) => {
          console.error("[TaxOfficeStore] Failed to refresh Intercom:", error);
          // Don't throw - this is a background operation
        });
      }
    } catch (error) {
      // Revert on failure
      set({ data: currentData });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update tax office data";
      set({ error: errorMessage });
      console.error("Failed to update tax office data:", error);
      throw error;
    }
  },

  /**
   * Sets the tax office data directly without persisting to backend.
   * Useful for initializing data from external sources.
   *
   * @param data - The tax office data to set
   */
  setData: (data: TaxOfficeData) => {
    set({ data, error: null });
  },

  /**
   * Resets the store to its initial state.
   */
  reset: () => {
    set(initialState);
  },
}));
